import { useEffect, useRef, useState, useLayoutEffect, useMemo, useCallback } from "react";
import PropTypes from 'prop-types';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Scroll3DText from './Scroll3DText';
import FeedbackCharacter from './FeedbackCharacter';
import { AnimatePresence } from 'framer-motion';
import './button-bubbles.css';
import './submitted-celebration.css';
import './graph-bg.css';
import ModernSelect from './ModernSelect';
import GaugeSlider from './GaugeSlider';
import TextAnimate from './TextAnimate';

// Environment flag safe for CRA/Webpack and others (avoids import.meta)
const IS_DEV = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

// Toggle verbose logs for CMS data during development
// Enable by adding ?debugFeedback=1 to the URL or
// running localStorage.setItem('debugFeedback','1') in the console
const DEBUG_LOGS = (() => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const viaQuery = params.has('debugFeedback');
    const viaStorage = window.localStorage && window.localStorage.getItem('debugFeedback') === '1';
    return IS_DEV && (viaQuery || viaStorage);
  } catch {
    return false;
  }
})();

gsap.registerPlugin(ScrollTrigger);

// Example CMS data (replace with actual import or prop)

// Remove duplicate function definition
const FeedbackSection = ({ cmsData } = {}) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const textInnerRef = useRef(null);
  const formRef = useRef(null);
  const vectorRef = useRef(null);
  const vectorInnerRef = useRef(null);
  const shape1Ref = useRef(null);
  const shape2Ref = useRef(null);
  const shape3Ref = useRef(null);

  const [showThankYou, setShowThankYou] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isUserActive, setIsUserActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Client-side block: prevent re-submission for a short window
  const [isBlocked, setIsBlocked] = useState(false);
  const activeCardRef = useRef(null);
  const railRef = useRef(null);
  const activityTimerRef = useRef(null);
  // Local search text for college autocomplete UI
  const [searchText, setSearchText] = useState('');
  // Character pin handled via ScrollTrigger

  // Initialize form data based on CMS data structure
  // Support both old structure (program_name) and new Storyblok2 structure (Rating)
  const formFieldsArray = cmsData?.Rating || cmsData?.program_name || [];
  
  const initialFormData = formFieldsArray.reduce((acc, field) => {
    acc[field.name] = field.default_value || "";
    return acc;
  }, {}) || {};

  const [formData, setFormData] = useState(initialFormData);

  // On mount, check last submission time (localStorage) and block if within window
  useEffect(() => {
    try {
      const lastStr = window.localStorage.getItem('feedback_last_submit_ms');
      const last = lastStr ? Number(lastStr) : 0;
      if (last > 0) {
        const delta = Date.now() - last;
        if (delta < 8 * 60 * 60 * 1000) {
          setIsBlocked(true);
        }
      }
    } catch {}
  }, []);

  // (Removed jump-to-step handler - UI for step rail was removed but state and animations remain)

  // Build steps dynamically from CMS data
  // Support both old structure (program_name) and new Storyblok2 structure (Rating)
  const steps = useMemo(() => {
    const fieldsArray = cmsData?.Rating || cmsData?.program_name || [];
    return fieldsArray.map(field => ({
      key: field.name,
      label: field.label,
      type: field.type,
      placeholder: field.placeholder,
      required: field.required === "true",
      options: field.options ? field.options.map(opt => ({ 
        value: opt.value, 
        label: opt.text,
        dependentvalue: opt.dependentvalue || opt.Dependentvalue  // Handle both cases
      })) : [],
      dependentField: field.dependentField,
      dependentvalue: field.dependentvalue || field.Dependentvalue,  // Handle both lowercase and uppercase
      default_value: field.default_value
    }));
  }, [cmsData]);

  // Identify the college field for special autocomplete UI
  const isCollegeField = (step) => {
    if (!step) return false;
    const k = (step.key || '').toString().toLowerCase();
    const l = (step.label || '').toString().toLowerCase();
    return (k.includes('college') || l.includes('college')) && Array.isArray(step.options) && step.options.length > 0;
  };

  // Determine if a step has an answer provided
  const isStepAnswered = (step) => {
    if (!step) return false;
    const val = formData[step.key];
    switch (step.type) {
      case 'select':
        // Reject empty, null, undefined, and the string "null" (used as fallback when no options)
        if (!val || val === 'null' || val === '') return false;
        return true;
      case 'slider':
        return val !== undefined && val !== null && val !== '';
      case 'textarea':
      case 'text':
      default:
        // For text fields, also check if it's a valid non-empty string
        if (typeof val === 'string') {
          const trimmed = val.trim();
          return trimmed.length > 0 && trimmed !== 'null';
        }
        return !!val;
    }
  };

  // Helper function to check if dependentvalue is empty
  const isDependentValueEmpty = (rawValue) => {
    if (!rawValue) return true;
    if (typeof rawValue !== 'string') return true;
    if (rawValue === '""') return true;
    if (rawValue === '"\"\""') return true;
    if (rawValue === '\\"\\"\\"') return true;
    if (rawValue.replace(/["\\]/g, '').trim() === '') return true;
    return false;
  };

  // Filter only visible steps - recalculate when formData changes
  // Supports multiple dependentFields separated by comma (OR logic - show if ANY matches)
  const visibleSteps = useMemo(() => {
    const filtered = steps.filter(step => {
      // DEBUG: Log dependentvalue for phone_number field
      if (step.key === 'phone_number' || step.name === 'phone_number') {
        console.log('📱 Phone field check:', {
          key: step.key,
          dependentField: step.dependentField,
          dependentvalue: step.dependentvalue,
          dependentvalueType: typeof step.dependentvalue,
          dependentvalueJSON: JSON.stringify(step.dependentvalue)
        });
      }
      
      // If field has NO dependent field configured, always show it
      if (!step.dependentField || step.dependentField.trim() === '') {
        return true;
      }
      
      // If field depends on itself (self-reference), always show it
      if (step.dependentField === step.key) {
        return true;
      }
      
      // Check if dependentvalue is empty - if so, hide the field
      if (isDependentValueEmpty(step.dependentvalue)) {
        return false;
      }
      
      // If dependentvalue is "*", always show this field
      if (step.dependentvalue.trim() === '*') {
        return true;
      }
      
      // Support multiple dependentFields and dependentvalues (comma separated)
      // OR logic: show if ANY condition matches
      const dependentFields = step.dependentField.split(',').map(f => f.trim());
      const dependentValues = step.dependentvalue.split(',').map(v => v.trim());
      
      // Check each pair - return true if ANY matches
      for (let i = 0; i < dependentFields.length; i++) {
        const field = dependentFields[i];
        const value = dependentValues[i] || dependentValues[0]; // Use first value if not enough values provided
        
        // Skip if field doesn't exist
        const fieldExists = steps.some(s => s.key === field);
        if (!fieldExists) continue;
        
        // If value is "*", this condition matches
        if (value === '*') {
          return true;
        }
        
        // Check if current form value matches
        if (formData[field] === value) {
          return true;
        }
      }
      
      // No condition matched
      return false;
    });
    return filtered;
  }, [steps, formData]);

  // Check if field should be visible based on dependencies
  // Supports multiple dependentFields separated by comma (OR logic - show if ANY matches)
  const isFieldVisible = useCallback((step) => {
    // If field has NO dependent field configured, always show it
    if (!step.dependentField || step.dependentField.trim() === '') {
      return true;
    }
    
    // If field depends on itself (self-reference), always show it
    if (step.dependentField === step.key) {
      return true;
    }
    
    // Check if dependentvalue is empty - if so, hide the field
    if (isDependentValueEmpty(step.dependentvalue)) {
      return false;
    }
    
    // If dependentvalue is "*", always show this field
    if (step.dependentvalue.trim() === '*') {
      return true;
    }
    
    // Support multiple dependentFields and dependentvalues (comma separated)
    // OR logic: show if ANY condition matches
    const dependentFields = step.dependentField.split(',').map(f => f.trim());
    const dependentValues = step.dependentvalue.split(',').map(v => v.trim());
    
    // Check each pair - return true if ANY matches
    for (let i = 0; i < dependentFields.length; i++) {
      const field = dependentFields[i];
      const value = dependentValues[i] || dependentValues[0];
      
      // Skip if field doesn't exist
      const fieldExists = steps.some(s => s.key === field);
      if (!fieldExists) continue;
      
      // If value is "*", this condition matches
      if (value === '*') {
        return true;
      }
      
      // Check if current form value matches
      if (formData[field] === value) {
        return true;
      }
    }
    
    // No condition matched
    return false;
  }, [formData, steps]);

  // Filter options within a select field based on their dependentvalue property
  // Supports comma-separated dependentvalues (OR logic - show if parent matches ANY)
  const getFilteredOptions = useCallback((step) => {
    if (!step.options || !Array.isArray(step.options)) {
      return [];
    }
    
    // If field has NO dependentField, show all options
    if (!step.dependentField || step.dependentField.trim() === '') {
      return step.options;
    }
    
    // If field depends on itself (self-reference like college_name), show all options
    if (step.dependentField === step.key) {
      return step.options;
    }
    
    const parentValue = formData[step.dependentField];
    
    return step.options.filter(option => {
      // If option doesn't have a dependentvalue property at all, always show it
      if (!option.hasOwnProperty('dependentvalue')) {
        return true;
      }
      
      // If dependentvalue is empty/null, DON'T show it
      // Handle various empty formats: "", "\"\"", '""', null, undefined, whitespace
      const rawValue = option.dependentvalue;
      const isEmpty = !rawValue || 
                      rawValue === '""' || 
                      rawValue === '"\"\""' ||
                      rawValue === '\\"\\"\\"' ||
                      rawValue.replace(/["\\]/g, '').trim() === '';
      if (isEmpty) {
        return false;
      }
      
      // If dependentvalue is "*", always show it
      if (option.dependentvalue.trim() === '*') {
        return true;
      }
      
      // Support comma-separated dependentvalues (OR logic)
      // Option shows if parent value matches ANY of the comma-separated values
      const allowedValues = option.dependentvalue.split(',').map(v => v.trim());
      return allowedValues.includes(parentValue);
    });
  }, [formData]);

  const handleInputChange = (field, value) => {
    // Log field name and value
    console.log('📝', field, ':', value);
    
    setFormData(prev => ({ ...prev, [field]: value }));
    // Trigger character activity
    setIsUserActive(true);
    clearTimeout(activityTimerRef.current);
    activityTimerRef.current = setTimeout(() => setIsUserActive(false), 1500);
  };

  // Trigger character activity on focus/click events
  const handleFocus = () => {
    setIsUserActive(true);
    clearTimeout(activityTimerRef.current);
    activityTimerRef.current = setTimeout(() => setIsUserActive(false), 1500);
  };

  // Auto-select any select-type fields that have only one option
  // Use all steps but respect dependency visibility
  useEffect(() => {
    setFormData(prev => {
      let changed = false;
      const next = { ...prev };
      steps.forEach(step => {
        const visible = isFieldVisible(step);
        if (!visible) return;
        if (step?.type === 'select' && Array.isArray(step.options)) {
          const filteredOptions = getFilteredOptions(step);
          if (filteredOptions.length === 1) {
            const only = filteredOptions[0];
            if (!prev[step.key]) {
              next[step.key] = only.value;
              changed = true;
            }
          } else if (filteredOptions.length === 0) {
            // No options: assign default_value if available, otherwise string 'null' so dropdown shows something
            if (!prev[step.key]) {
              const fallback = (step.default_value !== undefined && step.default_value !== null && step.default_value !== '')
                ? String(step.default_value)
                : 'null';
              next[step.key] = fallback;
              changed = true;
            }
          }
        } else if (step?.type === 'text') {
          // For Program Name (text type), auto-fill default_value and make it read-only in UI
          const label = (step.label || '').toString();
          if (label === 'Program Name') {
            const fallback = (step.default_value !== undefined && step.default_value !== null)
              ? String(step.default_value)
              : '';
            if (!prev[step.key] && fallback !== '') {
              next[step.key] = fallback;
              changed = true;
            }
          }
        }
      });
      return changed ? next : prev;
    });
  }, [steps, formData, isFieldVisible, getFilteredOptions]);



  // Clamp current step if filtering reduced visible steps
  useEffect(() => {
    if (currentStep >= visibleSteps.length && visibleSteps.length > 0) {
      setCurrentStep(visibleSteps.length - 1);
    }
  }, [visibleSteps, currentStep]);
  
  const handleNext = async () => {
    // Prevent duplicate submissions
    if (isSubmitting) return;
    // Respect client-side block
    if (isBlocked) {
      alert('Please wait a few minutes before submitting again.');
      return;
    }
    
    // If we're on the last step, submit the form
    if (currentStep === visibleSteps.length - 1) {
      setIsSubmitting(true);
      
      try {
        const payload = steps.reduce((acc, step) => {
          const visible = isFieldVisible(step);
          acc[step.key] = visible ? formData[step.key] : null;
          return acc;
        }, {});

        // Explicitly include 'score' if a slider step exists and has been committed via Next
        const sliderStep = steps.find(s => s.type === 'slider');
        if (sliderStep) {
          const val = formData[sliderStep.key];
          if (val !== undefined && val !== null && val !== '') {
            payload.score = val;
          }
        }

        const response = await fetch(
          "https://thebettertomorrow.in/apiforms/api/feedback",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        const result = await response.json();

        if (response.ok && result?.success) {
          setShowThankYou(true);
          // Persist current timestamp to start block window
          try {
            window.localStorage.setItem('feedback_last_submit_ms', Date.now().toString());
            setIsBlocked(true);
          } catch {}
        } else {
          throw new Error(result?.error || "Failed to submit feedback");
        }
      } catch (error) {
        console.error("Error submitting feedback:", error);
        alert("An error occurred while submitting feedback. Please try again.");
        setIsSubmitting(false);
      }
    } else {
      // Just move to next step
      setCurrentStep(s => s + 1);
    }
  };

  useEffect(() => {
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches; // lg and up
    const ctx = gsap.context(() => {
      if (!isDesktop) {
        // Mobile/tablet: ensure elements are visible, not offset, and form doesn't overlap
        gsap.set([textInnerRef.current, vectorInnerRef.current], { opacity: 1, x: 0, y: 0 });
        gsap.set(formRef.current, { opacity: 1, x: 0, y: 0, clearProps: 'transform' });
        return;
      }

  // Desktop-only animated entry and scrubs
  // 3D text and character: fade/slide up as before
  gsap.set([textInnerRef.current], { opacity: 0, y: 120 });
  // Capture character's base position from existing CSS transforms (e.g., Tailwind translate classes)
  const charBaseX = parseFloat(gsap.getProperty(vectorInnerRef.current, 'x')) || 0;
  const charBaseY = parseFloat(gsap.getProperty(vectorInnerRef.current, 'y')) || 0;
  // Character: start far left, will animate much further to the right
  gsap.set(vectorInnerRef.current, { opacity: 0, x: charBaseX - 500, y: charBaseY + 120 });
  
  // Capture base position from existing CSS transforms (e.g., Tailwind translate classes)
  const baseX = parseFloat(gsap.getProperty(formRef.current, 'x')) || 0;
  const baseY = parseFloat(gsap.getProperty(formRef.current, 'y')) || 0;
  // Form: start off-screen to the right and slightly down for a diagonal path, relative to base
  gsap.set(formRef.current, { opacity: 0, x: baseX + 220, y: baseY + 80 });

      gsap.set(shape1Ref.current, { opacity: 0, scale: 0.8, rotation: -15 });
      gsap.set(shape2Ref.current, { opacity: 0, scale: 0.6, rotation: 20 });
      gsap.set(shape3Ref.current, { opacity: 0, scale: 0.9, rotation: -10 });

      ScrollTrigger.create({
        trigger: containerRef.current,
        start: 'top 85%',
        end: 'top 25%',
        scrub: 1.5,
        onUpdate: (self) => {
          const p = self.progress;
          const y = 120 - p * 120;
          const opacity = p;
          gsap.to(textInnerRef.current, { opacity, y, duration: 0.3, ease: 'power2.out' });
          gsap.to(shape1Ref.current, { opacity: opacity * 0.6, scale: 0.8 + (p * 0.2), rotation: -15 + (p * 15), duration: 0.4, ease: 'power2.out' });
          gsap.to(shape2Ref.current, { opacity: opacity * 0.4, scale: 0.6 + (p * 0.4), rotation: 20 - (p * 20), duration: 0.5, ease: 'power2.out', delay: 0.1 });
          gsap.to(shape3Ref.current, { opacity: opacity * 0.5, scale: 0.9 + (p * 0.1), rotation: -10 + (p * 10), duration: 0.6, ease: 'power2.out', delay: 0.15 });
        },
      });

      // FeedbackCharacter: fade from 0 to 100% and move from left to much further right
        // Remove fade in/out effect: only animate position, keep opacity at 1
        ScrollTrigger.create({
          trigger: containerRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 2.5,
          onUpdate: (self) => {
            const p = self.progress;
            // Animate from far left to much further right (past base position by +250px)
            const x = (charBaseX - 500) + (p * 750); // moves from left to charBaseX + 250
            const y = (charBaseY + 120) - (p * 120); // moves up to charBaseY
            gsap.to(vectorInnerRef.current, { opacity: 1, y, x, duration: 0.4, ease: 'power2.out' });
          },
        });

      // Dedicated diagonal motion for the form across the whole section
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: 'top 85%',
        end: 'bottom top',
        scrub: 1.2,
        onUpdate: (self) => {
          const p = self.progress;
          // Nudge final position slightly further left than original base
          const extraLeft = 24; // px
          const xForm = (baseX + 220) - ((220 + extraLeft) * p);
          const yForm = (baseY + 80) - (80 * p);
          gsap.to(formRef.current, { x: xForm, y: yForm, opacity: 1, duration: 0.2, ease: 'none' });
        },
      });

      // Note: Keep FeedbackCharacter faded initially and reveal on initial scroll via the entry trigger above.

      // Pin the 3D text near the top (10px) while scrolling through the section (desktop only)
      if (isDesktop) {
        ScrollTrigger.create({
          trigger: textRef.current,
          start: 'top 10px',
          endTrigger: containerRef.current,
          end: 'bottom top+=10',
          pin: true,
          pinSpacing: false,
          anticipatePin: 1,
        });
      }
    }, containerRef);
    return () => ctx.revert();
  }, []);

  // Pin FeedbackCharacter 20px from viewport bottom; end at section bottom
  useEffect(() => {
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches; // lg and up
    if (!isDesktop) return; // no pin on mobile/tablet to avoid overlap
    if (!vectorRef.current || !containerRef.current) return;
    const st = ScrollTrigger.create({
      trigger: vectorRef.current,
      start: 'bottom bottom-=20',
      endTrigger: containerRef.current,
      end: 'bottom bottom-=20',
      pin: true,
      pinSpacing: false,
      pinReparent: true,
      anticipatePin: 1,
    });
    return () => st.kill();
  }, []);

   const isLastStep = currentStep === visibleSteps.length - 1;

  // Helper: force a manual soft wrap by inserting a newline at the last space
  // before a chosen character limit to guarantee multi-line rendering without overflow
  const formatLabel = (label, limit = 56) => {
    if (!label || typeof label !== 'string') return '';
    if (label.length <= limit) return label;
    const breakIndex = label.lastIndexOf(' ', limit);
    if (breakIndex > 0) {
      return label.slice(0, breakIndex) + '\n' + label.slice(breakIndex + 1);
    }
    // If no space found (single long word), allow anywhere wrap via CSS
    return label;
  };

  // Animate active card IN on step change (modern text-first transition)
  useLayoutEffect(() => {
    if (!activeCardRef.current) return;
    const el = activeCardRef.current;
    const title = el.querySelector('[data-role="title"]');
    const body = el.querySelector('[data-role="body"]');
    if (!title || !body) return;

    // improved entry animation: deeper 3D lift with perspective
    gsap.set(el, { transformPerspective: 1000, transformStyle: 'preserve-3d' });
  // Only animate the body; let TextAnimate handle the title label effect
  gsap.set([body], { y: 18, opacity: 0, filter: 'blur(8px)' });
    const tl = gsap.timeline();
    // entry: 3D tilt + slight rotationY and an overshoot scale with elastic settle
    tl.fromTo(
      el,
      { rotationX: 18, rotationY: 8, y: 14, scale: 0.985, opacity: 0.88, transformOrigin: '50% 60%' },
      { rotationX: -6, rotationY: -3, y: -6, scale: 1.03, opacity: 1, duration: 0.36, ease: 'power3.out' }
    )
      .to(el, { rotationX: 0, rotationY: 0, y: 0, scale: 1, duration: 0.34, ease: 'elastic.out(1, 0.6)' }, '>-0.02');

    // Note: Do not animate left-side vector on step change; keep transitions scoped to form card only

    // reveal text after the card settles
  tl.to([body], { y: 0, opacity: 1, filter: 'blur(0px)', duration: 0.42, ease: 'power3.out' }, '<0.06');

    // Animate the newly added item in the completed rail
    if (railRef.current && currentStep > 0) {
      const newItem = railRef.current.querySelector(`[data-rail-index="${currentStep - 1}"]`);
      if (newItem) {
        gsap.from(newItem, { x: -12, y: 12, opacity: 0, duration: 0.35, ease: 'power2.out' });
      }
    }
  }, [currentStep]);

  // After switching steps, try to focus the primary field for quick editing
  useEffect(() => {
    const t = setTimeout(() => {
      const el = activeCardRef.current;
      if (!el) return;
      const input = el.querySelector('input, textarea, select, [contenteditable="true"], [tabindex]:not([tabindex="-1"])');
      if (input && typeof input.focus === 'function') {
        // Try to focus without causing the browser to scroll. Older browsers may throw,
        // so fall back to the plain focus() call.
        try {
          input.focus({ preventScroll: true });
        } catch {
          input.focus();
        }
      }
    }, 120);
    return () => clearTimeout(t);
  }, [currentStep]);

  // Animate active card OUT with text-first blur/fade, then advance
  const animateAndAdvance = () => {
    const el = activeCardRef.current;
    if (!el) return handleNext();
    setIsTransitioning(true);
    const title = el.querySelector('[data-role="title"]');
    const body = el.querySelector('[data-role="body"]');
    const parts = [title, body].filter(Boolean);
    const tl = gsap.timeline({ onComplete: () => { handleNext(); setIsTransitioning(false); } });
    if (parts.length) {
      tl.to(parts, { y: -12, opacity: 0, filter: 'blur(8px)', duration: 0.22, ease: 'power2.in', stagger: 0.02 });
    }
    // Note: Removed vectorRef exit/return motions to avoid re-animating left visuals on each input
    // flip-away exit along Y for a card-like turn
    tl.to(el, { rotationY: 70, x: 40, opacity: 0, duration: 0.48, ease: 'power3.in' }, '<0.02');
  };

  return (
  <section ref={containerRef} className="relative min-h-screen py-0 overflow-hidden graph-bg full-bleed">
      {/* DESKTOP LAYOUT - Hidden on mobile/tablet */}
      <div className="hidden xl:block">
        {/* 3D Text for Desktop */}
        <div className="relative z-20 w-full px-0 pt-4">
          <div ref={textRef} className="mb-10 text-center text-white">
            <div ref={textInnerRef} className="inline-block">
              <Scroll3DText text="Learning Review Portal" />
            </div>
          </div>
        </div>

        <div className="relative z-10 w-full px-0">
          <div className="flex flex-row items-start">
            {/* Character Section - Desktop */}
            <div className="flex relative w-[35%] pr-0 min-h-[320px] items-center justify-start mb-0">
              <div ref={vectorRef} className="relative z-0 pointer-events-none">
                <div ref={vectorInnerRef} className="scale-90 translate-x-32">
                  <FeedbackCharacter isActive={isUserActive} lookUp={isUserActive} />
                </div>
              </div>
            </div>

            {/* Form Section - Desktop */}
            <div className="w-[65%] flex justify-end mt-24">
              <div ref={formRef} className="relative z-20 w-full max-w-4xl p-6 px-10 pr-20 xl:pr-32 rounded-2xl -translate-x-16 xl:-translate-x-28 min-h-[440px] mx-auto">
            <AnimatePresence mode="wait" initial={false}>
              {!showThankYou ? (
                <div className="relative flex-1 min-h-[300px] md:min-h-[360px] md:h-[380px]">
                  {/* Active Form (left progress UI removed to simplify layout) */}
                  <div className="relative w-full">
                    {/* Fixed nav arrows over the card: not part of the animated card, so they don't transition */}
                    {!isBlocked && (
                    <div className="absolute top-0 right-0 z-30 flex items-center gap-1.5 md:gap-2">
                      <button
                        onClick={() => { handleFocus(); setCurrentStep(s => Math.max(0, s - 1)); }}
                        disabled={currentStep === 0 || isTransitioning}
                        className={`w-8 h-8 md:w-10 md:h-10 rounded-full border-2 flex items-center justify-center transition-all duration-300 focus:outline-none ${
                          (currentStep === 0 || isTransitioning)
                            ? 'border-white/20 text-white/30 cursor-not-allowed'
                            : [
                                // Filled by default when enabled
                                'text-white border-transparent bg-gradient-to-r from-sky-500 to-indigo-500 shadow-[0_4px_18px_rgba(59,130,246,0.45)]',
                                // Hover: brighten
                                'hover:from-sky-400 hover:to-indigo-400 hover:shadow-[0_6px_24px_rgba(59,130,246,0.55)]',
                                // Focus: outline ring
                                'focus:ring-2 focus:ring-sky-300/70',
                                // Active: slightly pressed
                                'active:scale-95 active:shadow-[0_3px_14px_rgba(59,130,246,0.5)]'
                              ].join(' ')
                        }`}
                        aria-label="Previous step"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-4 md:h-4">
                          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => { handleFocus(); setCurrentStep(s => Math.min(visibleSteps.length - 1, s + 1)); }}
                        disabled={
                          currentStep === visibleSteps.length - 1 ||
                          isTransitioning ||
                          (visibleSteps[currentStep]?.required && !isStepAnswered(visibleSteps[currentStep]))
                        }
                        className={`w-8 h-8 md:w-10 md:h-10 rounded-full border-2 flex items-center justify-center transition-all duration-300 focus:outline-none ${
                          (currentStep === visibleSteps.length - 1 || isTransitioning || (visibleSteps[currentStep]?.required && !isStepAnswered(visibleSteps[currentStep])))
                            ? 'border-white/20 text-white/30 cursor-not-allowed'
                            : [
                                // Filled by default when enabled
                                'text-white border-transparent bg-gradient-to-r from-sky-500 to-indigo-500 shadow-[0_4px_18px_rgba(59,130,246,0.45)]',
                                // Hover: brighten
                                'hover:from-sky-400 hover:to-indigo-400 hover:shadow-[0_6px_24px_rgba(59,130,246,0.55)]',
                                // Focus: outline ring
                                'focus:ring-2 focus:ring-sky-300/70',
                                // Active: slightly pressed
                                'active:scale-95 active:shadow-[0_3px_14px_rgba(59,130,246,0.5)]'
                              ].join(' ')
                        }`}
                        aria-label="Next step"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-4 md:h-4">
                          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    )}
                    <div
                      key={visibleSteps[currentStep]?.key || visibleSteps[currentStep]?.label || currentStep}
                      ref={activeCardRef}
                      className="h-full p-2 bg-transparent border-0 rounded-none shadow-none md:p-4"
                    >
                      {(() => {
                         const step = visibleSteps[currentStep];
                        if (!step) return null;
                          if (isBlocked) {
                            return (
                              <div className="flex flex-col items-center justify-center gap-6 py-8 md:py-12">
                                {/* Checkmark icon */}
                                <div className="relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24">
                                  <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-2xl animate-pulse" />
                                  <svg className="relative z-10 checkmark-svg" viewBox="0 0 100 100" width="80" height="80" aria-hidden="true">
                                    <circle cx="50" cy="50" r="45" className="checkmark-bg" />
                                    <path 
                                      className="checkmark-path" 
                                      d="M30 52 L45 67 L72 35"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                                <div className="px-4 text-center">
                                  <h2 className="mb-2 text-2xl font-bold text-transparent md:text-4xl bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">
                                    Already Submitted!
                                  </h2>
                                  <p className="mb-1 text-base md:text-lg text-white/80">Your feedback has already been submitted</p>
                                </div>
                              </div>
                            );
                          }
                        return (
                          <div className="flex flex-col h-full">
                            {/* Header */}
                            <div className="mb-4 md:mb-6">
                              <div className="flex items-center gap-2 mb-2 md:gap-3">
                                {/* Text wraps naturally within container */}
                                <div className="w-full min-h-[48px] md:min-h-[64px] pr-20">
                                  <h3
                                    data-role="title"
                                    className="w-full text-lg font-bold leading-snug md:text-2xl text-sky-300"
                                    style={{ 
                                      wordBreak: 'break-word', 
                                      overflowWrap: 'break-word', 
                                      whiteSpace: 'normal',
                                      display: 'block'
                                    }}
                                  >
                                    <TextAnimate
                                      key={step.key || step.label}
                                      animation="blurInUp"
                                      by="word"
                                      duration={0.6}
                                    >
                                      {formatLabel(step.label)}
                                    </TextAnimate>
                                  </h3>
                                  {/* removed required/optional helper text to simplify UI */}
                                </div>
                                {/* Arrows moved to fixed overlay above; left empty here */}
                              </div>
                            </div>

                            {/* Input Area */}
                            <div data-role="body" className="flex flex-col flex-1">
                             {isCollegeField(step) ? (
                                <div className="relative flex flex-col flex-1">
                                  {(() => {
                                    const filteredOptions = getFilteredOptions(step);
                                    if (filteredOptions.length === 1) {
                                      // Single option available: show as selected, no dropdown/search
                                      return (
                                        <div className="flex items-center justify-between w-full px-4 py-3 text-white border rounded-xl border-white/10 bg-white/5">
                                          <span className="text-xs tracking-wide uppercase text-white/50">Selected</span>
                                          <span className="font-medium">{filteredOptions[0]?.label}</span>
                                        </div>
                                      );
                                    }
                                    
                                    return (
                                      <>
                                        <div className="relative">
                                          <input
                                            type="text"
                                            value={searchText}
                                            onChange={e => setSearchText(e.target.value)}
                                            onFocus={handleFocus}
                                            placeholder={step.placeholder || 'Type your college name'}
                                            className="w-full px-3 py-2 text-base text-white transition-all duration-300 bg-transparent border-b-[2.5px] rounded-none md:px-4 md:py-3 md:text-lg placeholder-white/30 border-white/20 focus:outline-none focus:border-sky-400/70 focus:border-b-[2.5px] focus:shadow-none"
                                            autoComplete="off"
                                          />
                                        </div>
                                        {(searchText?.length > 0) && filteredOptions.length > 0 && (
                                          <ul className="absolute left-0 right-0 z-50 mt-2 overflow-auto text-sm border rounded-md backdrop-blur-sm border-white/10 max-h-48 md:max-h-60 top-full md:text-base" style={{ backgroundColor: 'rgba(5, 56, 89, 0.6)' }}>
                                            {filteredOptions
                                              .filter(opt => opt.label.toLowerCase().includes((searchText || '').toLowerCase()))
                                              .slice(0, 6)
                                              .map(opt => (
                                                <li key={opt.value}>
                                                  <button
                                                    type="button"
                                                    className="w-full px-3 py-2 text-left text-white transition-colors md:px-4 md:py-3 hover:bg-sky-500/20"
                                                    onClick={() => {
                                                    handleFocus();
                                                    // Store the value for logic/dependency, display label in input
                                                    handleInputChange(step.key, opt.value);
                                                    setSearchText(opt.label);
                                                    setTimeout(() => animateAndAdvance(), 240);
                                                  }}
                                                >
                                                  {opt.label}
                                                </button>
                                              </li>
                                            ))}
                                        </ul>
                                      )}
                                    </>
                                  );
                                  })()}
                                </div>
                              ) : step.type === 'slider' ? (
                                <div className="flex flex-col items-center justify-center flex-1">
                                  <GaugeSlider
                                    onChange={undefined}
                                    onFocus={handleFocus}
                                    onNext={(finalScore) => {
                                      handleInputChange(step.key, finalScore);
                                      animateAndAdvance();
                                    }}
                                  />
                                </div>
                              ) : step.type === 'select' ? (
                                <div className="flex-1">
                                  {(() => {
                                    const filteredOptions = getFilteredOptions(step);
                                    const isZeroOptions = filteredOptions.length === 0;
                                    const isSingleOption = filteredOptions.length === 1;
                                    
                                    if (isSingleOption) {
                                      // Single option available: show as selected, no dropdown
                                      return (
                                        <div className="flex items-center justify-between w-full px-4 py-3 text-white border rounded-xl border-white/10 bg-white/5">
                                          <span className="text-xs tracking-wide uppercase text-white/50">Selected</span>
                                          <span className="font-medium">{filteredOptions[0]?.label}</span>
                                        </div>
                                      );
                                    }
                                    
                                    const display = isZeroOptions
                                      ? String((step.default_value !== undefined && step.default_value !== null && step.default_value !== '') ? step.default_value : 'null')
                                      : null;
                                    const optionsForSelect = isZeroOptions
                                      ? [{ value: display, label: display }]
                                      : filteredOptions;
                                    const valueForSelect = isZeroOptions
                                      ? (formData[step.key] ?? display)
                                      : formData[step.key];
                                    return (
                                      <ModernSelect
                                        value={valueForSelect}
                                        onChange={(val) => {
                                          handleInputChange(step.key, val);
                                          setTimeout(() => animateAndAdvance(), 240);
                                        }}
                                        onFocus={handleFocus}
                                        placeholder={step.placeholder}
                                        options={optionsForSelect}
                                        disabled={isZeroOptions}
                                      />
                                    );
                                  })()}
                                </div>
                              ) : step.type === 'textarea' ? (
                                <div className="flex flex-col flex-1 gap-3 md:gap-4">
                                  <textarea
                                    rows={5}
                                    value={formData[step.key]}
                                    onChange={(e) => handleInputChange(step.key, e.target.value)}
                                    onFocus={handleFocus}
                                    placeholder={step.placeholder}
                                    minLength={1}
                                    maxLength={600}
                                    className="flex-1 px-3 py-3 text-base text-white transition-all duration-300 bg-transparent border-2 resize-none md:px-5 md:py-4 md:text-lg placeholder-white/30 rounded-xl border-white/10 focus:outline-none focus:border-sky-400/60 focus:shadow-none"
                                  />
                                  <div className="flex items-center justify-between">
                                    <div className="text-xs text-white/50">
                                      {formData[step.key]?.length || 0} characters
                                    </div>
                                    <button
                                      onClick={() => animateAndAdvance()}
                                      disabled={(step.required && !formData[step.key]?.trim()) || isSubmitting}
                                      className="relative group"
                                    >
                                      <div
                                        className={`bubble-button px-4 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base font-semibold transition-all duration-300 ${
                                          (step.required && !formData[step.key]?.trim()) || isSubmitting
                                            ? 'bg-white/10 text-white/40 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white shadow-[0_10px_40px_rgba(59,130,246,0.4)] hover:shadow-[0_15px_50px_rgba(59,130,246,0.5)] hover:scale-105'
                                        }`}
                                      >
                                        <span className="relative z-10 flex items-center gap-1.5 md:gap-2">
                                          {isSubmitting ? (
                                            <>
                                              <svg
                                                className="w-3.5 h-3.5 md:w-4 md:h-4 text-white animate-spin"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                              >
                                                <circle
                                                  className="opacity-25"
                                                  cx="12"
                                                  cy="12"
                                                  r="10"
                                                  stroke="currentColor"
                                                  strokeWidth="4"
                                                ></circle>
                                                <path
                                                  className="opacity-75"
                                                  fill="currentColor"
                                                  d="M4 12a8 8 0 018-8v4l-3 3-3-3V4z"
                                                ></path>
                                              </svg>
                                              <span>Submitting...</span>
                                            </>
                                          ) : (
                                            <>
                                              {isLastStep ? 'Submit Feedback' : 'Next'}
                                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-4 md:h-4">
                                                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              </svg>
                                            </>
                                          )}
                                        </span>
                                        <div className="bubbles">
                                          {Array.from({ length: 24 }).map((_, i) => (
                                            <span className="bubble" key={i}></span>
                                          ))}
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              ) : step.type === 'text' ? (
                                (() => {
                                  const isProgramName = step.label === 'Program Name';
                                  if (isProgramName) {
                                    return (
                                      <div className="flex flex-col gap-3 md:gap-4">
                                        <div className="flex items-center justify-start w-full px-4 py-3 text-white border rounded-xl border-white/10 bg-white/5">
                                          <span className="text-lg font-medium md:text-xl">
                                            {formData[step.key] || step.default_value || ''}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <div></div>
                                          <button
                                            onClick={() => animateAndAdvance()}
                                            disabled={isSubmitting}
                                            className="relative group"
                                          >
                                            <div
                                              className={`bubble-button px-4 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base font-semibold transition-all duration-300 ${
                                                isSubmitting
                                                  ? 'bg-white/10 text-white/40 cursor-not-allowed'
                                                  : 'bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white shadow-[0_10px_40px_rgba(59,130,246,0.4)] hover:shadow-[0_15px_50px_rgba(59,130,246,0.5)] hover:scale-105'
                                              }`}
                                            >
                                              <span className="relative z-10 flex items-center gap-1.5 md:gap-2">
                                                Next
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-4 md:h-4">
                                                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                              </span>
                                              <div className="bubbles">
                                                {Array.from({ length: 24 }).map((_, i) => (
                                                  <span className="bubble" key={i}></span>
                                                ))}
                                              </div>
                                            </div>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="flex flex-col gap-3 md:gap-4">
                                      <input
                                        type="text"
                                        value={formData[step.key]}
                                        onChange={(e) => handleInputChange(step.key, e.target.value)}
                                        onFocus={handleFocus}
                                        placeholder={step.placeholder}
                                        className="w-full px-3 py-2 text-base text-white transition-all duration-300 bg-transparent border-b-[2.5px] rounded-none md:px-4 md:py-3 md:text-lg placeholder-white/30 border-white/20 focus:outline-none focus:border-sky-400/70 focus:border-b-[2.5px] focus:shadow-none"
                                      />
                                      <div className="flex items-center justify-end">
                                        <button
                                          onClick={() => animateAndAdvance()}
                                          disabled={(step.required && !formData[step.key]?.trim()) || isSubmitting}
                                          className="relative group"
                                        >
                                          <div
                                            className={`bubble-button px-4 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base font-semibold transition-all duration-300 ${
                                              (step.required && !formData[step.key]?.trim()) || isSubmitting
                                                ? 'bg-white/10 text-white/40 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white shadow-[0_10px_40px_rgba(59,130,246,0.4)] hover:shadow-[0_15px_50px_rgba(59,130,246,0.5)] hover:scale-105'
                                            }`}
                                          >
                                            <span className="relative z-10 flex items-center gap-1.5 md:gap-2">
                                              {isLastStep ? 'Submit Feedback' : 'Next'}
                                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-4 md:h-4">
                                                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              </svg>
                                            </span>
                                            <div className="bubbles">
                                              {Array.from({ length: 24 }).map((_, i) => (
                                                <span className="bubble" key={i}></span>
                                              ))}
                                            </div>
                                          </div>
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })()
                              ) : null}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative min-h-[320px] md:h-[380px]">
                  <div className="flex flex-col items-center justify-center w-full h-full gap-4 md:gap-6">
                    {/* Animated checkmark */}
                    <div className="relative">
                      <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-2xl animate-pulse" />
                      <svg className="relative z-10 checkmark-svg" viewBox="0 0 100 100" width="100" height="100" aria-hidden="true">
                        <circle cx="50" cy="50" r="45" className="checkmark-bg" />
                        <path 
                          className="checkmark-path" 
                          d="M30 52 L45 67 L72 35"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="px-4 text-center">
                      <h2 className="mb-2 text-2xl font-bold text-transparent md:text-4xl bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">
                        Thank You!
                      </h2>
                      <p className="mb-1 text-base md:text-lg text-white/80">Your feedback has been submitted</p>
                      <p className="text-sm text-white/60">We appreciate you taking the time to share your thoughts</p>
                    </div>
                    {/* Success particles */}
                    <div className="flex gap-2 text-xl md:text-2xl animate-bounce" style={{ animationDelay: '0.2s' }}>
                      🎉 ✨ 🎊
                    </div>
                  </div>
                </div>
              )}
            </AnimatePresence>
          </div>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE/TABLET LAYOUT - Hidden on desktop */}
      <div className="block xl:hidden">
        {/* 3D Text for Mobile/Tablet */}
        <div className="relative w-full px-4 pt-16 pb-6 mt-9">
          <div className="text-center text-white">
            <div className="inline-block">
              <Scroll3DText text="Learning Review Portal" />
            </div>
          </div>
        </div>

        {/* Form Section - Mobile/Tablet */}
        <div className="w-full px-4 pb-4">
          <div className="relative w-full max-w-4xl mx-auto">
            <AnimatePresence mode="wait" initial={false}>
              {!showThankYou ? (
                <div className="relative flex-1 min-h-[300px]">
                  {/* Copy the same form content here but without refs for mobile */}
                  <div className="relative w-full">
                    <div className="absolute top-0 right-0 z-30 flex items-center gap-1.5">
                      <button
                        onClick={() => { handleFocus(); setCurrentStep(s => Math.max(0, s - 1)); }}
                        onFocus={handleFocus}
                        disabled={currentStep === 0 || isTransitioning}
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 focus:outline-none ${
                          (currentStep === 0 || isTransitioning)
                            ? 'border-white/20 text-white/30 cursor-not-allowed'
                            : 'text-white border-transparent bg-gradient-to-r from-sky-500 to-indigo-500 shadow-[0_4px_18px_rgba(59,130,246,0.45)] hover:from-sky-400 hover:to-indigo-400 hover:shadow-[0_6px_24px_rgba(59,130,246,0.55)] focus:ring-2 focus:ring-sky-300/70 active:scale-95'
                        }`}
                        aria-label="Previous step"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => { handleFocus(); setCurrentStep(s => Math.min(visibleSteps.length - 1, s + 1)); }}
                        onFocus={handleFocus}
                        disabled={
                          currentStep === visibleSteps.length - 1 ||
                          isTransitioning ||
                          (visibleSteps[currentStep]?.required && !isStepAnswered(visibleSteps[currentStep]))
                        }
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 focus:outline-none ${
                          (currentStep === visibleSteps.length - 1 || isTransitioning || (visibleSteps[currentStep]?.required && !isStepAnswered(visibleSteps[currentStep])))
                            ? 'border-white/20 text-white/30 cursor-not-allowed'
                            : 'text-white border-transparent bg-gradient-to-r from-sky-500 to-indigo-500 shadow-[0_4px_18px_rgba(59,130,246,0.45)] hover:from-sky-400 hover:to-indigo-400 hover:shadow-[0_6px_24px_rgba(59,130,246,0.55)] focus:ring-2 focus:ring-sky-300/70 active:scale-95'
                        }`}
                        aria-label="Next step"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <div
                      key={visibleSteps[currentStep]?.key || visibleSteps[currentStep]?.label || currentStep}
                      ref={activeCardRef}
                      className="h-full p-2 bg-transparent border-0 rounded-none shadow-none"
                    >
                      {(() => {
                         const step = visibleSteps[currentStep];
                        if (!step) return null;
                        return (
                          <div className="flex flex-col h-full">
                            <div className="mb-4">
                              <div className="flex items-center gap-2">
                                <div className="w-full min-h-[48px] pr-12">
                                  <h3
                                    data-role="title"
                                    className="w-full text-lg font-bold leading-snug text-sky-300"
                                    style={{ 
                                      wordBreak: 'break-word', 
                                      overflowWrap: 'break-word', 
                                      whiteSpace: 'normal',
                                      display: 'block'
                                    }}
                                  >
                                    <TextAnimate
                                      key={step.key || step.label}
                                      animation="blurInUp"
                                      by="word"
                                      duration={0.6}
                                    >
                                      {formatLabel(step.label)}
                                    </TextAnimate>
                                  </h3>
                                </div>
                              </div>
                            </div>

                            {/* Input Area - Same as desktop */}
                            <div data-role="body" className="flex flex-col flex-1">
                             {isCollegeField(step) ? (
                                <div className="relative flex flex-col flex-1">
                                  {(() => {
                                    const filteredOptions = getFilteredOptions(step);
                                    if (filteredOptions.length === 1) {
                                      return (
                                        <div className="flex items-center justify-between w-full px-4 py-3 text-white border rounded-xl border-white/10 bg-white/5">
                                          <span className="text-xs tracking-wide uppercase text-white/50">Selected</span>
                                          <span className="font-medium">{filteredOptions[0]?.label}</span>
                                        </div>
                                      );
                                    }
                                    
                                    return (
                                      <>
                                        <div className="relative">
                                          <input
                                            type="text"
                                            value={searchText}
                                            onChange={e => setSearchText(e.target.value)}
                                            onFocus={handleFocus}
                                            placeholder={step.placeholder || 'Type your college name'}
                                            className="w-full px-3 py-2 text-base text-white transition-all duration-300 bg-transparent border-b-[2.5px] rounded-none placeholder-white/30 border-white/20 focus:outline-none focus:border-sky-400/70 focus:border-b-[2.5px] focus:shadow-none"
                                            autoComplete="off"
                                          />
                                        </div>
                                        {(searchText?.length > 0) && filteredOptions.length > 0 && (
                                          <ul className="absolute left-0 right-0 z-50 mt-2 overflow-auto text-sm border rounded-md backdrop-blur-sm border-white/10 max-h-48 top-full" style={{ backgroundColor: 'rgba(5, 56, 89, 0.6)' }}>
                                            {filteredOptions
                                              .filter(opt => opt.label.toLowerCase().includes((searchText || '').toLowerCase()))
                                              .slice(0, 6)
                                              .map(opt => (
                                                <li key={opt.value}>
                                                  <button
                                                    type="button"
                                                    className="w-full px-3 py-2 text-left text-white transition-colors hover:bg-sky-500/20"
                                                    onClick={() => {
                                                      handleFocus();
                                                      handleInputChange(step.key, opt.value);
                                                      setSearchText(opt.label);
                                                      setTimeout(() => animateAndAdvance(), 240);
                                                    }}
                                                  >
                                                    {opt.label}
                                                  </button>
                                                </li>
                                              ))}
                                          </ul>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              ) : step.type === 'slider' ? (
                                <div className="flex flex-col items-center justify-center flex-1">
                                  <GaugeSlider
                                    onChange={undefined}
                                    onFocus={handleFocus}
                                    onNext={(finalScore) => {
                                      handleInputChange(step.key, finalScore);
                                      animateAndAdvance();
                                    }}
                                  />
                                </div>
                              ) : step.type === 'select' ? (
                                <div className="flex-1">
                                  {(() => {
                                    const filteredOptions = getFilteredOptions(step);
                                    const isZeroOptions = filteredOptions.length === 0;
                                    const isSingleOption = filteredOptions.length === 1;
                                    
                                    if (isSingleOption) {
                                      return (
                                        <div className="flex items-center justify-between w-full px-4 py-3 text-white border rounded-xl border-white/10 bg-white/5">
                                          <span className="text-xs tracking-wide uppercase text-white/50">Selected</span>
                                          <span className="font-medium">{filteredOptions[0]?.label}</span>
                                        </div>
                                      );
                                    }
                                    
                                    const display = isZeroOptions
                                      ? String((step.default_value !== undefined && step.default_value !== null && step.default_value !== '') ? step.default_value : 'null')
                                      : null;
                                    const optionsForSelect = isZeroOptions
                                      ? [{ value: display, label: display }]
                                      : filteredOptions;
                                    const valueForSelect = isZeroOptions
                                      ? (formData[step.key] ?? display)
                                      : formData[step.key];
                                    return (
                                      <ModernSelect
                                        value={valueForSelect}
                                        onChange={(val) => {
                                          handleInputChange(step.key, val);
                                          setTimeout(() => animateAndAdvance(), 240);
                                        }}
                                        onFocus={handleFocus}
                                        placeholder={step.placeholder}
                                        options={optionsForSelect}
                                        disabled={isZeroOptions}
                                      />
                                    );
                                  })()}
                                </div>
                              ) : step.type === 'textarea' ? (
                                <div className="flex flex-col flex-1 gap-3">
                                  <textarea
                                    rows={5}
                                    value={formData[step.key]}
                                    onChange={(e) => handleInputChange(step.key, e.target.value)}
                                    onFocus={handleFocus}
                                    placeholder={step.placeholder}
                                    minLength={1}
                                    maxLength={600}
                                    className="flex-1 px-3 py-3 text-base text-white transition-all duration-300 bg-transparent border-2 resize-none placeholder-white/30 rounded-xl border-white/10 focus:outline-none focus:border-sky-400/60 focus:shadow-none"
                                  />
                                  <div className="flex items-center justify-between">
                                    <div className="text-xs text-white/50">
                                      {formData[step.key]?.length || 0} characters
                                    </div>
                                    <button
                                      onClick={() => animateAndAdvance()}
                                      disabled={(step.required && !formData[step.key]?.trim()) || isSubmitting}
                                      className="relative group"
                                    >
                                      <div
                                        className={`bubble-button px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
                                          (step.required && !formData[step.key]?.trim()) || isSubmitting
                                            ? 'bg-white/10 text-white/40 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white shadow-[0_10px_40px_rgba(59,130,246,0.4)] hover:shadow-[0_15px_50px_rgba(59,130,246,0.5)] hover:scale-105'
                                        }`}
                                      >
                                        <span className="relative z-10 flex items-center gap-1.5">
                                          {isSubmitting ? (
                                            <>
                                              <svg
                                                className="w-3.5 h-3.5 text-white animate-spin"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                              >
                                                <circle
                                                  className="opacity-25"
                                                  cx="12"
                                                  cy="12"
                                                  r="10"
                                                  stroke="currentColor"
                                                  strokeWidth="4"
                                                ></circle>
                                                <path
                                                  className="opacity-75"
                                                  fill="currentColor"
                                                  d="M4 12a8 8 0 018-8v4l-3 3-3-3V4z"
                                                ></path>
                                              </svg>
                                              <span>Submitting...</span>
                                            </>
                                          ) : (
                                            <>
                                              {isLastStep ? 'Submit Feedback' : 'Next'}
                                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              </svg>
                                            </>
                                          )}
                                        </span>
                                        <div className="bubbles">
                                          {Array.from({ length: 24 }).map((_, i) => (
                                            <span className="bubble" key={i}></span>
                                          ))}
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              ) : step.type === 'text' ? (
                                (() => {
                                  const isProgramName = step.label === 'Program Name';
                                  if (isProgramName) {
                                    return (
                                      <div className="flex flex-col gap-3">
                                        <div className="flex items-center justify-start w-full px-4 py-3 text-white border rounded-xl border-white/10 bg-white/5">
                                          <span className="text-lg font-medium">
                                            {formData[step.key] || step.default_value || ''}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <div></div>
                                          <button
                                            onClick={() => animateAndAdvance()}
                                            disabled={isSubmitting}
                                            className="relative group"
                                          >
                                            <div
                                              className={`bubble-button px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
                                                isSubmitting
                                                  ? 'bg-white/10 text-white/40 cursor-not-allowed'
                                                  : 'bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white shadow-[0_10px_40px_rgba(59,130,246,0.4)] hover:shadow-[0_15px_50px_rgba(59,130,246,0.5)] hover:scale-105'
                                              }`}
                                            >
                                              <span className="relative z-10 flex items-center gap-1.5">
                                                Next
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                              </span>
                                              <div className="bubbles">
                                                {Array.from({ length: 24 }).map((_, i) => (
                                                  <span className="bubble" key={i}></span>
                                                ))}
                                              </div>
                                            </div>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="flex flex-col gap-3">
                                      <input
                                        type="text"
                                        value={formData[step.key]}
                                        onChange={(e) => handleInputChange(step.key, e.target.value)}
                                        onFocus={handleFocus}
                                        placeholder={step.placeholder}
                                        className="w-full px-3 py-2 text-base text-white transition-all duration-300 bg-transparent border-b-[2.5px] rounded-none placeholder-white/30 border-white/20 focus:outline-none focus:border-sky-400/70 focus:border-b-[2.5px] focus:shadow-none"
                                      />
                                      <div className="flex items-center justify-end">
                                        <button
                                          onClick={() => animateAndAdvance()}
                                          disabled={(step.required && !formData[step.key]?.trim()) || isSubmitting}
                                          className="relative group"
                                        >
                                          <div
                                            className={`bubble-button px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
                                              (step.required && !formData[step.key]?.trim()) || isSubmitting
                                                ? 'bg-white/10 text-white/40 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white shadow-[0_10px_40px_rgba(59,130,246,0.4)] hover:shadow-[0_15px_50px_rgba(59,130,246,0.5)] hover:scale-105'
                                            }`}
                                          >
                                            <span className="relative z-10 flex items-center gap-1.5">
                                              {isLastStep ? 'Submit Feedback' : 'Next'}
                                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              </svg>
                                            </span>
                                            <div className="bubbles">
                                              {Array.from({ length: 24 }).map((_, i) => (
                                                <span className="bubble" key={i}></span>
                                              ))}
                                            </div>
                                          </div>
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })()
                              ) : null}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative min-h-[320px]">
                  <div className="flex flex-col items-center justify-center w-full h-full gap-4">
                    <div className="relative">
                      <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-2xl animate-pulse" />
                      <svg className="relative z-10 checkmark-svg" viewBox="0 0 100 100" width="100" height="100" aria-hidden="true">
                        <circle cx="50" cy="50" r="45" className="checkmark-bg" />
                        <path 
                          className="checkmark-path" 
                          d="M30 52 L45 67 L72 35"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="px-4 text-center">
                      <h2 className="mb-2 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">
                        Thank You!
                      </h2>
                      <p className="mb-1 text-base text-white/80">Your feedback has been submitted</p>
                      <p className="text-sm text-white/60">We appreciate you taking the time to share your thoughts</p>
                    </div>
                    <div className="flex gap-2 text-xl animate-bounce" style={{ animationDelay: '0.2s' }}>
                      🎉 ✨ 🎊
                    </div>
                  </div>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Character Section - Mobile/Tablet */}
        <div className="relative z-10 flex items-center justify-center w-full px-4 -mt-20 -mb-6 pointer-events-none">
          <div className="relative scale-75 sm:scale-90">
            <FeedbackCharacter isActive={true} lookUp={isUserActive} />
          </div>
        </div>
      </div>
    </section>
  );
};

FeedbackSection.propTypes = {
  cmsData: PropTypes.shape({
    program_name: PropTypes.arrayOf(
      PropTypes.shape({
        options: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            text: PropTypes.string,
          })
        ),
      })
    ),
  }),
};
// defaultProps removed: using default parameter in function signature instead
export default FeedbackSection;